import React, { useEffect, useRef, useState } from "react";
import "./ShopScrollSection.css";
import { trackEvent } from "../../utils/pageMeta";

const SHOP_URL = "https://lys-sylt.myshopify.com";
const trackShopClick = () => trackEvent("click_shop_partner", { partner: "lys-sylt" });

// ponytail: frames live in src/images/shop-scroll/, named frame-0001.webp .. frame-00NN.webp
// (zero-padded, sequential). Drop files matching that pattern in and the scroll-scrub
// activates automatically - no code change needed. Until then this renders a static teaser.
const frameContext = require.context("../../images/shop-scroll", false, /^\.\/frame-\d+\.(webp|jpe?g|png)$/);
const FRAME_PATHS = frameContext.keys().sort().map((key) => frameContext(key));

function ShopScrollSection() {
    const wrapperRef = useRef(null);
    const canvasRef = useRef(null);
    const titleRef = useRef(null);
    const ctaRef = useRef(null);
    const imagesRef = useRef([]);
    const [framesReady, setFramesReady] = useState(false);

    // preload every frame on every device; the static poster below covers the
    // wait so the section is never blank or half-loaded, on mobile or desktop
    useEffect(() => {
        if (FRAME_PATHS.length === 0) return;
        let loaded = 0;
        let cancelled = false;
        FRAME_PATHS.forEach((src, i) => {
            const img = new window.Image();
            img.onload = img.onerror = () => {
                loaded += 1;
                if (!cancelled && loaded === FRAME_PATHS.length) setFramesReady(true);
            };
            img.src = src;
            imagesRef.current[i] = img;
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // scroll-driven canvas scrub, active only while the section is near the viewport
    useEffect(() => {
        if (!framesReady) return;
        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        if (!wrapper || !canvas) return;
        const ctx = canvas.getContext("2d");
        let frameRequested = false;

        function resizeCanvas() {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = canvas.clientWidth * dpr;
            canvas.height = canvas.clientHeight * dpr;
        }

        function drawFrame(index) {
            const img = imagesRef.current[index];
            if (!img || !img.complete || img.naturalWidth === 0) return;
            const cw = canvas.width;
            const ch = canvas.height;
            const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
            const w = img.naturalWidth * scale;
            const h = img.naturalHeight * scale;
            ctx.clearRect(0, 0, cw, ch);
            ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
        }

        function update() {
            frameRequested = false;
            const rect = wrapper.getBoundingClientRect();
            const scrollable = rect.height - window.innerHeight;
            const progress = scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 0;

            const frameIndex = Math.min(FRAME_PATHS.length - 1, Math.floor(progress * FRAME_PATHS.length));
            drawFrame(frameIndex);

            if (titleRef.current) {
                const inOpacity = Math.min(1, progress / 0.12);
                const outOpacity = progress > 0.35 ? Math.max(0, 1 - (progress - 0.35) / 0.15) : 1;
                titleRef.current.style.opacity = Math.min(inOpacity, outOpacity);
            }
            if (ctaRef.current) {
                ctaRef.current.style.opacity = progress > 0.72 ? Math.min(1, (progress - 0.72) / 0.15) : 0;
            }
        }

        function onScroll() {
            if (!frameRequested) {
                frameRequested = true;
                requestAnimationFrame(update);
            }
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    resizeCanvas();
                    update();
                    window.addEventListener("scroll", onScroll, { passive: true });
                    window.addEventListener("resize", resizeCanvas);
                } else {
                    window.removeEventListener("scroll", onScroll);
                    window.removeEventListener("resize", resizeCanvas);
                }
            },
            { rootMargin: "200px 0px" }
        );
        observer.observe(wrapper);

        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", resizeCanvas);
        };
    }, [framesReady]);

    // ponytail: no frames dropped in yet -> static teaser instead of a broken/empty section
    if (FRAME_PATHS.length === 0) {
        return (
            <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="shop-scroll shop-scroll--static" onClick={trackShopClick}>
                <div className="shop-scroll__overlay">
                    <span className="shop-scroll__eyebrow">LYS EDITIONS</span>
                    <h2>Sylt in limitierten Fine-Art-Prints</h2>
                    <span className="shop-scroll__cta shop-scroll__cta--visible">Zum Shop →</span>
                </div>
            </a>
        );
    }

    // poster-first: show the last frame as a plain background immediately, on every
    // device, and only switch to the interactive scroll-scrub once all frames are
    // loaded in the background - never a blank or half-loaded section
    if (!framesReady) {
        return (
            <a
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="shop-scroll shop-scroll--static"
                style={{ backgroundImage: `url(${FRAME_PATHS[FRAME_PATHS.length - 1]})` }}
                onClick={trackShopClick}
            >
                <div className="shop-scroll__overlay">
                    <span className="shop-scroll__eyebrow">LYS EDITIONS</span>
                    <h2>Sylt in limitierten Fine-Art-Prints</h2>
                    <span className="shop-scroll__cta shop-scroll__cta--visible">Zum Shop →</span>
                </div>
            </a>
        );
    }

    return (
        <div ref={wrapperRef} className="shop-scroll-wrapper">
            <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="shop-scroll" aria-label="Zum LYS Editions Shop" onClick={trackShopClick}>
                <canvas ref={canvasRef} className="shop-scroll__canvas" />
                <div className="shop-scroll__overlay">
                    <div ref={titleRef} className="shop-scroll__title">
                        <span className="shop-scroll__eyebrow">LYS EDITIONS</span>
                        <h2>Jedes Bild hat eine Geschichte.</h2>
                    </div>
                    <div ref={ctaRef} className="shop-scroll__cta-box">
                        <h3>Entdecke die ganze Wand voller Sylt-Momente</h3>
                        <span className="shop-scroll__cta shop-scroll__cta--visible">Zum Shop →</span>
                    </div>
                </div>
            </a>
        </div>
    );
}

export default ShopScrollSection;
